import { useContext, useEffect, useMemo, useState } from 'react';
import { selectGraphsEditable, selectGraphsStatus } from '@/redux/selectors/dashboardSelectors';
import { setGraphsEditable } from '@/redux/slices/dashboardSlice';
import styled from '@emotion/styled';
import { Button, Grid } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { DragDropProvider } from '@dnd-kit/react';
import { move, swap } from '@dnd-kit/helpers';
import { WidgetContainer } from '@/components/Dashboard/WidgetContainer';
import { graphDirectory } from '@/components/Dashboard/DefaultGraphs';
import { ConfigContext } from '@/contextAPI/configContext';
import { DragDropEvents } from '@dnd-kit/abstract';
import { DragDropManager, Draggable, Droppable } from '@dnd-kit/dom';
import Panel from './Panel';
import Pane from './Pane';
import useCurrentBreakpoint from '@/components/_hooks/useCurrentBreakpoint';

export type DndKitEvents = DragDropEvents<Draggable, Droppable, DragDropManager>;

export enum DragTypes {
  panel = 'panel',
  card = 'card'
}

export const PLACEHOLDER_CHART = 'placeholder';

type Item = {
  id: string;
  list: string[];
};

const transformStringsToRecord = (items: string[][]): Item[] => {
  return items.map((row) => ({
    id: `row-${row.join('-')}`,
    list: row
  }));
};

const transformItemsToRecord = (item: Item[]): Record<string, string[]> => {
  const record: Record<string, string[]> = {};
  item.forEach((row) => {
    record[row.id] = row.list;
  });

  return record;
};

const WidgetFrame = () => {
  const dispatch = useAppDispatch();
  const status = useAppSelector(selectGraphsStatus);
  const editable = useAppSelector(selectGraphsEditable);
  const hasEmptyRow = editable.order.some((row) => row.length === 0);
  const dashboardConfig = useContext(ConfigContext)?.dashboardConfig;
  const maxPerRow = dashboardConfig?.maxGraphsPerRow || 3;
  const currBreakpoint = useCurrentBreakpoint();
  const items = useMemo(() => transformStringsToRecord(editable.order), [editable.order]);
  const [dropTarget, setDropTarget] = useState<Record<number, boolean>>({});

  useEffect(() => {
    const elements = document.querySelectorAll('.staggered-sash');
    elements.forEach((element, index) => {
      if (index % 2 === 1) {
        // index is zero-based, so even elements are at odd indices
        element.classList.add('even');
      } else {
        element.classList.add('odd');
      }
    });
  }, [editable.order, status.editable, currBreakpoint]);

  const setItems = (values: Item[]) => {
    dispatch(
      setGraphsEditable({
        ...editable,
        orderBuildType: 'custom',
        order: values.map((item) => item.list)
      })
    );
  };

  const onAddRow = () => {
    setItems([
      ...items,
      {
        id: `row-${PLACEHOLDER_CHART}`,
        list: [PLACEHOLDER_CHART]
      }
    ]);
  };

  const onDropTarget = (flag: boolean, index: number) => {
    setDropTarget((val) => ({
      ...val,
      [index]: flag
    }));
  };

  const getRowCharts = (ids: string[]) => {
    return ids.map((chartId) => {
      if (chartId === PLACEHOLDER_CHART) {
        return {
          id: PLACEHOLDER_CHART,
          label: PLACEHOLDER_CHART,
          fullRow: false,
          render: () => <EmptyMessage hovering={'hovering'}>Drag here</EmptyMessage>
        };
      }

      return graphDirectory[chartId as keyof typeof graphDirectory];
    });
  };

  const onDeletePanel = (index: number) => {
    const filtered = items.filter((_, i) => i !== index);
    setItems(filtered);
  };

  const onDragOver: DndKitEvents['dragover'] = (event) => {
    const { source, target } = event.operation;
    event.preventDefault();

    if (!source || !target || source.id === target.id) {
      return;
    }

    if (event.operation?.source?.data?.type === DragTypes.panel) {
      const panels = swap(
        items.map((panel) => panel.id),
        event
      );
      setItems(
        panels.map((panelId) => {
          const list = items.find((item) => item.id === panelId)?.list ?? [];

          return {
            id: panelId,
            list
          };
        })
      );
    } else {
      const group = event.operation?.target?.data?.group as string;
      const activeCard = graphDirectory[source.id];

      if (activeCard && activeCard.fullRow) {
        return;
      }

      const targetPanelIndex = items.findIndex(({ id }) => id === group);
      const targetPanel = items[targetPanelIndex];

      if (targetPanel) {
        if (targetPanel.list.length >= maxPerRow && !targetPanel.list.includes(source.id as string)) {
          return;
        }

        const panelCharts = getRowCharts(targetPanel.list);

        if (panelCharts.some((chart) => chart.fullRow)) {
          return;
        }
      }

      const itemsRecord: Record<string, string[]> = transformItemsToRecord(items);
      const updatedItemsRecord = move(itemsRecord, event);
      const updatedItems: Item[] = [];
      for (const key in updatedItemsRecord) {
        const list = updatedItemsRecord[key];

        if (list.length === 0) {
          list.splice(0, 0, PLACEHOLDER_CHART);
        } else if (list.length > 1 && list.includes(PLACEHOLDER_CHART)) {
          list.splice(list.indexOf(PLACEHOLDER_CHART), 1);
        }

        const id = `row-${list.join('-')}`;

        updatedItems.push({
          id,
          list
        });
      }

      setItems(updatedItems);
    }
  };

  return (
    <Frame view={status.editable ? 'editable' : 'read-only'}>
      <DragDropProvider onDragOver={onDragOver}>
        {items.map((row, colIndex) => {
          return (
            <Panel
              count={row.list.filter((item) => item !== PLACEHOLDER_CHART).length}
              isChildDropTarget={!!dropTarget[colIndex]}
              key={row.id}
              id={row.id}
              index={colIndex}
              onDelete={() => onDeletePanel(colIndex)}
            >
              {getRowCharts(row.list).map((chart, index) => (
                <WidgetContainer
                  noDrag={chart.fullRow}
                  onDropTarget={(flag) => onDropTarget(flag, colIndex)}
                  key={chart.id}
                  id={chart.id}
                  index={index}
                  group={row.id}
                >
                  {chart.id === PLACEHOLDER_CHART ? chart.render() : <Pane key={chart.label} pane={chart} />}
                </WidgetContainer>
              ))}
            </Panel>
          );
        })}
        {status.editable && (
          <AddRowButton disabled={hasEmptyRow} variant={'outlined'} size="large" onClick={onAddRow}>
            Add New Row
          </AddRowButton>
        )}
      </DragDropProvider>
    </Frame>
  );
};

export default WidgetFrame;

const staggeredSpacing = [45, 55]; // percentage
const Frame = styled(Grid)<{ view: 'editable' | 'read-only' }>`
  display: flex;
  flex-direction: column;

  .MuiGrid-root {
    margin: 0px;
  }

  .panel {
    margin-bottom: ${({ theme }) => theme.spacing(3)};
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    // Staggered & Equal Spacing styling
    ${({ view }) => {
      return (
        view === 'read-only' &&
        `
        > * .staggered-sash {
          overflow: none;

          &.odd {
              .pane {
                  width: ${staggeredSpacing[0]}%;
                }
                .pane:nth-of-type(2) {
                  width: ${staggeredSpacing[1]}%;
                }
          }
          &.even {
            .pane {
              width: ${staggeredSpacing[1]}%;
            }
            .pane:nth-of-type(2) {
              width: ${staggeredSpacing[0]}%;
            }
      }
        }
      `
      );
    }}
  }
`;

const AddRowButton = styled(Button)`
  width: 300px;
  margin: auto;
  margin-top: ${({ theme }) => theme.spacing(2)};
  text-transform: uppercase;
`;

const EmptyMessage = styled.h2<{ hovering: 'hovering' | 'not-hovering' }>`
  color: ${({ theme }) => theme.palette.text.secondary};
  opacity: ${({ hovering }) => (hovering === 'hovering' ? 1 : 0.5)};
  width: 100%;
  margin: ${({ theme }) => theme.spacing(0, 2, 2, 2)};
  padding: ${({ theme }) => theme.spacing(2)};
  border: 2px dashed;
  border-color: ${({ theme }) => theme.palette.secondary.main};
`;
